import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  Typography,
  styled,
  Stack,
  useTheme,
  TextField,
  Button
} from "@mui/material";
import { CheckmarkCircle24Px, Error24Px } from "@qubit/autoparts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import TestingToolLayout from "~/features/testingTools/TestingToolLayout";
import {
  addEvent,
  resetSingleStepSession,
  selectScannerTestSession,
  setSingleStepTestStatus
} from "~/features/testingTools/testingTools.slice";
import { TestingTool } from "~/features/testingTools/testingTools.types";
import { BarcodeSvg } from "~/icons/BarcodeSvg";

import { useBarcodeScanner, useKeyDownHandler } from "~/lib/barCodeScan";

const BarcodeContainer = styled(Stack)(
  ({ theme: { palette, spacing, shadows } }) => ({
    border: `1px solid ${palette.gray.dark}`,
    borderRadius: 10,
    padding: spacing(3),
    boxShadow: shadows["1"],
    backgroundColor: palette.secondary.main,
    alignItems: "center",
    justifyContent: "space-evenly",
    flexDirection: "column",
    gap: spacing(3),
    minWidth: 550
  })
);
const OverheadScannerContainer = styled(Stack)(
  ({ theme: { palette, spacing } }) => ({
    border: `1px solid ${palette.darkGray.main}`,
    borderRadius: 10,
    padding: spacing(3),
    alignItems: "center",
    justifyContent: "space-evenly",
    flexDirection: "column",
    minWidth: 550
  })
);

const CustomTextField = styled(TextField)(({ theme: { palette } }) => ({
  minWidth: 300,
  "& .MuiInputBase-root": {
    "&.Mui-focused:after": {
      borderBottom: `1px solid ${palette.darkGray.dark}`
    }
  },
  "& .MuiInputLabel-root": {
    fontSize: "1.625rem",
    "&.Mui-focused": {
      color: palette.text.primary
    }
  }
}));

const ScannerContainer = styled(Box)(({ theme: { spacing } }) => ({
  display: "grid",
  placeContent: "center",
  alignItems: "stretch",
  justifyItems: "center",
  height: "100%",
  rowGap: spacing(3),
  gridTemplateRows: "minmax(auto, 400px) 130px minmax(auto, 200px)"
}));

type ScannerTestProps = {
  onTestingSessionStart: (testType: TestingTool) => Promise<void>;
};
const ScannerTest = ({ onTestingSessionStart }: ScannerTestProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { palette } = useTheme();
  const [scanText, setScanText] = useState<string | null>(null);

  const scannerTestStatus = useAppSelector(selectScannerTestSession);
  const hasCurrentTestSuccedeed = scannerTestStatus.status === "completed";
  const hasCurrentTestFailed = scannerTestStatus.status === "error";
  const isCurrentTestCompleted =
    hasCurrentTestFailed || hasCurrentTestSuccedeed;

  useKeyDownHandler();
  useBarcodeScanner({
    findScanMatch: (match) => match,
    processScanMatch: (match) => setScanText(match),
    deps: [setScanText],
    disabled: hasCurrentTestFailed
  });

  const failTest = async () => {
    await onTestingSessionStart("Scanner");

    // setSingleStepTestStatus adds Failed event to the event feed
    // dispatch the “Scanner Failed” event in order after test is initialized
    dispatch(
      setSingleStepTestStatus({
        testingToolName: "Scanner",
        eventStatus: "Failed"
      })
    );
  };
  const retryTest = () => {
    if (hasCurrentTestSuccedeed) {
      setScanText("");
    }
    dispatch(resetSingleStepSession("Scanner"));
  };

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!scanText) return;

    void (async () => {
      await onTestingSessionStart("Scanner");

      // dispatch the “Success” event in order after test is initialized
      dispatch(
        addEvent({
          text: `Success Event - code: ${scanText}`,
          state: "Success",
          testOrigin: "Scanner"
        })
      );
      dispatch(
        setSingleStepTestStatus({
          testingToolName: "Scanner",
          eventStatus: "Success",
          skipLogging: true,
          additionalProps: { lastScannedBarcode: scanText }
        })
      );
    })();
  }, [dispatch, onTestingSessionStart, scanText]);
  const scannedBarcodeToDisplay =
    scanText || scannerTestStatus.lastScannedBarcode || "";

  return (
    <TestingToolLayout>
      <ScannerContainer aria-label="Scanner test content" role="region">
        <BarcodeContainer>
          <BarcodeSvg sx={{ width: 400, height: 200 }} />
          <Typography variant="h5">{t("scan barcode instructions")}</Typography>
        </BarcodeContainer>
        <OverheadScannerContainer>
          <CustomTextField
            variant="standard"
            label={t("scanned code")}
            type="text"
            value={scannedBarcodeToDisplay}
            aria-readonly
            fullWidth
            size="medium"
            color="primary"
            focused={!!scanText}
            slotProps={{
              htmlInput: {
                style: {
                  fontSize: "1.625rem",
                  textAlign: "center"
                }
              }
            }}
          />
        </OverheadScannerContainer>
        <Stack
          sx={{
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            alignSelf: "flex-end"
          }}
        >
          {!isCurrentTestCompleted && (
            <Button
              onClick={failTest}
              color="error"
              sx={{
                minHeight: 70,
                minWidth: 250,
                fontWeight: 600,
                fontSize: 20
              }}
            >
              {t("unable to scan barcode")}
            </Button>
          )}
          {hasCurrentTestFailed && (
            <Error24Px
              style={{
                fill: palette.error.main,
                height: "70px",
                width: "70px"
              }}
              role="img"
              aria-label="error-icon"
            />
          )}
          {hasCurrentTestSuccedeed && (
            <CheckmarkCircle24Px
              style={{
                fill: palette.success.main,
                height: "70px",
                width: "70px"
              }}
              role="img"
              aria-label="success-icon"
            />
          )}
          {isCurrentTestCompleted && (
            <Button
              onClick={retryTest}
              sx={{
                minHeight: 70,
                minWidth: 250,
                fontSize: "1.3rem",
                fontWeight: 600
              }}
              color="secondary"
              startIcon={
                <ReplayIcon
                  sx={{
                    color: "#000",
                    width: 40,
                    height: 40
                  }}
                />
              }
            >
              {t("retry test")}
            </Button>
          )}
        </Stack>
      </ScannerContainer>
    </TestingToolLayout>
  );
};

export default ScannerTest;
